import React, { useEffect, useState } from 'react';
import { TextField, Button, Grid2, Alert, Autocomplete, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportServices } from '../../services/reportServices';
import { useAuth } from '../../services/authentication/authContext';
import Clear from '@mui/icons-material/Clear';
import GetApp from '@mui/icons-material/GetApp';
import { downloadReport } from '../../utils/downloadReport ';
import '../../css/Reports.css';
import { jobServices } from '../../services/jobServices';
import ReportGenerateButton from '../common/ReportGenerateButton';
import PageHeader from '../ui/PageHeader';

const JobWiseReport = ({ showHeader = true }) => {

    const [clientList, setClientList] = useState([]);
    const [jobType, setJobType] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [jobData, setJobData] = useState([]);
    const [jobLoading, setJobLoading] = useState(false);
    const [selectedJobType, setSelectedJobType] = useState("");
    const [shortListedJobs, setShortListedJobs] = useState([]);
    const [selectedJobId, setSelectedJobId] = useState("");
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState('');
    const [generating, setGenerating] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const { accessToken } = useAuth();

    useEffect(() => {
        if (!accessToken) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch clients
                const clients = await jobServices.getClientsForJobTrackScreen();
                setClientList(clients);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setClientList([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

    }, [accessToken]);

    const handleClientChange = e => {
        const clientId = e.target.value;
        setSelectedClientId(clientId);
        setJobLoading(true);

        jobServices.getJobsByClientId(clientId)
            .then(jobs => {
                setJobData(jobs);
                // reset any job‐specific state:
                const typeList = [...new Set(jobs?.map(j => j?.type))];
                setJobType(typeList);
                setSelectedJobType('');
                setShortListedJobs([]);
                setSelectedJobId('');
            }).finally(() => {
                setJobLoading(true);
            })
    };

    const handleTypeChange = e => {
        const selectedType = e.target.value;
        setSelectedJobType(selectedType)
        const sortListedJobs = jobData?.filter(j => j?.type === selectedType);
        setShortListedJobs(sortListedJobs);
        setSelectedJobId('');
    };

    const handleJobChange = (e) => {
        const jobId = e.target.value;
        setSelectedJobId(jobId);
    };


    const handleClear = () => {
        setSelectedClientId('');
        setSelectedJobType('');
        setShortListedJobs([]);
        setSelectedJobId('');
    };

    const handleReport = () => {
        setGenerating(true);
        reportServices.downloadJobWiseReport(selectedJobId)
            .then((response) => {

                downloadReport(response);

                setGenerating(false);
                setSuccessMessage("Report Generated Successfully!");
                setTimeout(() => setSuccessMessage(null), 4000);

                setSelectedClientId('');
                setSelectedJobType('');
                setShortListedJobs([]);
                setSelectedJobId('');
            })
            .catch((error) => {
                setError(error.message);
                setTimeout(() => {
                    setError(null);
                }, 4000);
                setGenerating(false);
            });

    };

    return (
        <div className={showHeader ? "reports-container" : ""}>
            {showHeader && (
                <PageHeader title="Job-wise Report" />
            )}
            <Box className={showHeader ? "report-box" : ""}>

                {successMessage && (
                    <Alert variant="filled" severity="success" style={{ marginBottom: '1.25rem' }}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert variant="outlined" severity="error" style={{ marginBottom: '1.25rem' }}>
                        {error}
                    </Alert>
                )}

                <form style={{ marginTop: '1.25rem' }}>
                    <Grid2 container spacing={3}>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={clientList}
                                getOptionLabel={(option) => option.name || ''}
                                value={clientList.find(client => client.id === selectedClientId) || null}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleClientChange({ target: { value: newValue.id } });
                                        setShortListedJobs([]);
                                    } else {
                                        setSelectedClientId('');
                                        setJobData([]);
                                        setJobType([]);
                                        setShortListedJobs([]);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Client"
                                        margin="normal"
                                        fullWidth
                                    />
                                )}
                                disableClearable
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={jobType}
                                getOptionLabel={(option) => option || ''}
                                value={selectedJobType || null}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleTypeChange({ target: { value: newValue } });
                                    } else {
                                        setSelectedJobType('');
                                        setShortListedJobs([]);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Job Type"
                                        margin="normal"
                                        fullWidth
                                    />
                                )}
                                disableClearable
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={shortListedJobs}
                                getOptionLabel={(option) => option?.name || ''}
                                value={shortListedJobs.find(job => job.id === selectedJobId) || null}
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        handleJobChange({ target: { value: newValue.id } });
                                    } else {
                                        setSelectedJobId('');
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Job"
                                        margin="normal"
                                        fullWidth
                                    />
                                )}
                                disableClearable
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }} className="gridContainer">
                            <ReportGenerateButton
                                onClick={handleReport}
                                loading={generating}
                                disabled={!selectedJobId || (Boolean(loading) || generating)}
                                className={`commonButton ${selectedJobId ? 'reportButtonSuccess' : 'reportButtonDefault'}`}
                                icon={<GetApp className="buttonIcon" />}
                                label={t('job.report')}
                                loadingLabel="Preparing your report..."
                            />
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleClear}
                                className={`commonButton clearButton`}
                                disabled={(Boolean(loading) || generating)}
                            >
                                <Clear className="buttonIcon" /> {t('job.clear')}
                            </Button>
                        </Grid2>

                    </Grid2>
                </form>
            </Box>
        </div>
    );
};

export default JobWiseReport;